import { c as _c } from "react-compiler-runtime";
import { memo, useCallback, useContext } from 'react';
import { ROW } from '../../virtualization/types/observerTypes';
import { useObserveTableElementRef } from '../../virtualization/hooks/useObserveTableElementRef';
import { FullVirtualizationContext, useIsRowVisible, useVisibleColumnsIfRowVisible } from '../../virtualization/VirtualizationContext';
import { VIRTUAL_INDEX_ATTRIBUTE } from '../constants/virtualization';
import Cell from './Cell';
import SelectRowCheckboxCell from '../../selection/components/SelectRowCheckboxCell';
import { getCellContentId } from '../utils/cellIdUtils';
import { mergeRefs } from 'foundations-components/transitional/utils/mergeRefs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getColumnLayout(virtualColumnInfo, numberOfFrozenColumns) {
  const {
    virtualColumns,
    leftSpacerWidth,
    leftSpacerColspan,
    rightSpacerWidth,
    rightSpacerColspan
  } = virtualColumnInfo;
  const entries = [];
  for (let i = 0; i < numberOfFrozenColumns; i++) {
    entries.push({
      type: 'cell',
      columnIndex: i,
      isCellVisible: true
    });
  }
  if (leftSpacerColspan > 0) {
    entries.push({
      type: 'spacer',
      key: 'col-spacer-left',
      colspan: leftSpacerColspan,
      width: leftSpacerWidth
    });
  }
  for (const virtualColumn of virtualColumns) {
    entries.push({
      type: 'cell',
      columnIndex: virtualColumn.index + numberOfFrozenColumns,
      isCellVisible: true
    });
  }
  if (rightSpacerColspan > 0) {
    entries.push({
      type: 'spacer',
      key: 'col-spacer-right',
      colspan: rightSpacerColspan,
      width: rightSpacerWidth
    });
  }
  return entries;
}
const Row = t0 => {
  const $ = _c(37);
  const {
    rowId,
    rowIndex,
    row,
    columns,
    selectable,
    showSelectPlaceholder,
    RowComponent,
    numberOfFrozenColumns: t1,
    measureElement,
    virtualRowIndex,
    virtualColumnInfo
  } = t0;
  const numberOfFrozenColumns = t1 === undefined ? 0 : t1;
  const enableFullVirtualization = useContext(FullVirtualizationContext);
  const handleObserveRef = useObserveTableElementRef();
  const isRowVisible = useIsRowVisible(rowIndex);
  const visibleColumns = useVisibleColumnsIfRowVisible(isRowVisible);
  let t2;
  if ($[0] !== enableFullVirtualization || $[1] !== handleObserveRef || $[2] !== measureElement) {
    t2 = node => mergeRefs([enableFullVirtualization ? undefined : handleObserveRef, measureElement])(node);
    $[0] = enableFullVirtualization;
    $[1] = handleObserveRef;
    $[2] = measureElement;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const mergedRef = t2;
  const showSelectCell = selectable;
  const showSelectPlaceholderCell = !showSelectCell && showSelectPlaceholder;
  let t3;
  if ($[4] !== RowComponent || $[5] !== columns || $[6] !== isRowVisible || $[7] !== mergedRef || $[8] !== numberOfFrozenColumns || $[9] !== row || $[10] !== rowId || $[11] !== rowIndex || $[12] !== selectable || $[13] !== showSelectCell || $[14] !== showSelectPlaceholderCell || $[15] !== virtualColumnInfo || $[16] !== virtualRowIndex || $[17] !== visibleColumns) {
    const columnLayout = virtualColumnInfo ? getColumnLayout(virtualColumnInfo, numberOfFrozenColumns) : columns.map((_column, columnIndex) => ({
      type: "cell",
      columnIndex,
      isCellVisible: isRowVisible && visibleColumns.has(columnIndex)
    }));
    const t4 = `row-${rowId}`;
    const t5 = rowIndex % 2 === 0 ? "even" : "odd";
    let t6;
    if ($[19] !== rowIndex || $[20] !== t4 || $[21] !== t5 || $[22] !== virtualRowIndex) {
      t6 = {
        "data-test-id": t4,
        "data-observer-type": ROW,
        "data-observer-id": rowIndex,
        [VIRTUAL_INDEX_ATTRIBUTE]: virtualRowIndex,
        "data-fdt-row-parity": t5
      };
      $[19] = rowIndex;
      $[20] = t4;
      $[21] = t5;
      $[22] = virtualRowIndex;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== columns || $[25] !== rowId || $[26] !== rowIndex || $[27] !== showSelectCell) {
      t7 = showSelectCell && /*#__PURE__*/_jsx(SelectRowCheckboxCell, {
        rowId: rowId,
        rowIndex: rowIndex,
        firstCellContentId: columns[0] ? getCellContentId(columns[0].columnNamespace, columns[0].columnName, rowId) : undefined
      });
      $[24] = columns;
      $[25] = rowId;
      $[26] = rowIndex;
      $[27] = showSelectCell;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    let t8;
    if ($[29] !== showSelectPlaceholderCell) {
      t8 = showSelectPlaceholderCell && /*#__PURE__*/_jsx("td", {
        "data-selection-column": true
      });
      $[29] = showSelectPlaceholderCell;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    let t9;
    if ($[31] !== columns || $[32] !== row || $[33] !== rowId || $[34] !== rowIndex || $[35] !== selectable) {
      t9 = entry => {
        if (entry.type === "spacer") {
          return /*#__PURE__*/_jsx("td", {
            "aria-hidden": "true",
            colSpan: entry.colspan,
            style: {
              padding: 0,
              border: "none",
              width: entry.width,
              minWidth: entry.width,
              maxWidth: entry.width
            }
          }, entry.key);
        }
        const column = columns[entry.columnIndex];
        const {
          CellComponent,
          columnNamespace,
          columnName
        } = column;
        return /*#__PURE__*/_jsx(Cell, {
          columns: columns,
          columnIndex: entry.columnIndex,
          rowIndex: rowIndex,
          columnNamespace: columnNamespace,
          columnName: columnName,
          CellComponent: CellComponent,
          rowId: rowId,
          row: row,
          selectable: selectable,
          isCellVisible: entry.isCellVisible
        }, `${columnNamespace}-${columnName}-${rowId}`);
      };
      $[31] = columns;
      $[32] = row;
      $[33] = rowId;
      $[34] = rowIndex;
      $[35] = selectable;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    t3 = /*#__PURE__*/_jsxs(RowComponent, {
      row: row,
      trRef: mergedRef,
      requiredRowProps: t6,
      columns: columns,
      rowId: rowId,
      selectable: selectable,
      virtualRowIndex: virtualRowIndex,
      virtualColumnInfo: virtualColumnInfo,
      children: [t7, t8, columnLayout.map(t9)]
    });
    $[4] = RowComponent;
    $[5] = columns;
    $[6] = isRowVisible;
    $[7] = mergedRef;
    $[8] = numberOfFrozenColumns;
    $[9] = row;
    $[10] = rowId;
    $[11] = rowIndex;
    $[12] = selectable;
    $[13] = showSelectCell;
    $[14] = showSelectPlaceholderCell;
    $[15] = virtualColumnInfo;
    $[16] = virtualRowIndex;
    $[17] = visibleColumns;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
};
export default /*#__PURE__*/memo(Row);